// Feature #359 — SM Coverage: organisation-wide skills heatmap
// (pnc_officer / sm_manager). Rows are active technical skills, columns are
// ratings 1–5, each cell counts the people whose *current* rating on that
// skill is that value; hovering a cell lists them.
//
// Colour is measured across the whole grid: the busiest cell is greenest,
// a single person is reddest, empty cells stay neutral. Every cell prints
// its count, so colour is never the only signal (red/green colour blindness).
//
// #360 quick search filters rows by skill name as you type — a plain term
// matches anywhere in the name, and `*` stands for any run of characters.
// The colour scale stays on the whole grid, so filtering never recolours.
// #361 exports the rows currently shown (search, toggle and sort applied) as
// a CSV that opens straight in Excel, built from the payload already loaded.
import React, { useEffect, useMemo, useState } from 'react'
import { Download, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ui/PageHeader'
import { getSkillsCoverage, SmCoverage, SmCoverageCell, SmCoverageSkill } from '@/api/sm'
import { blockStyle, th, toggleButton, toggleGroup } from './components/SmShared'

const RATING_COL_WIDTH = 110

type SortMode ='coverage' | 'alpha'

interface HoverState {
  skill: string
  cell: SmCoverageCell
  x: number
  y: number
}

/** Red (hue 0) → green (hue 120), scaled on the whole grid's busiest cell. */
const cellColour = (count: number, max: number): string => {
  if (count === 0) return 'transparent'
  const t = max <= 1 ? 1 : (count - 1) / (max - 1)
  return `hsl(${Math.round(t * 120)}, 65%, 80%)`
}

const byName = (a: SmCoverageSkill, b: SmCoverageSkill) => a.name.localeCompare(b.name)

/** Case-insensitive, unanchored matcher: `py` and `*py*` both find
 *  "Python"; `data*eng` finds "Data Engineering". Null when the box is empty. */
const skillMatcher = (term: string): RegExp | null => {
  const t = term.trim()
  if (!t) return null
  const pattern = t
    .split('*')
    .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*')
  return new RegExp(pattern, 'i')
}

/** One CSV field: quoted when needed, and defused if Excel would read it as
 *  a formula (skill names are user-entered). */
const csvField = (value: string | number): string => {
  let s = String(value)
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const levelHeading = (lvl: SmCoverage['levels'][number]) =>
  lvl.label ? `${lvl.value} – ${lvl.label}` : String(lvl.value)

/** Counts per rating, then totals, then who holds each rating. */
const coverageCsv = (levels: SmCoverage['levels'], rows: SmCoverageSkill[]): string => {
  const header = [
    'Skill',
    ...levels.map(levelHeading),
    'Rated people',
    'Coverage',
    ...levels.map((lvl) => `${levelHeading(lvl)} – people`),
  ]
  const lines = rows.map((skill) => [
    skill.name,
    ...skill.cells.map((c) => c.count),
    skill.rated_count,
    skill.coverage,
    ...skill.cells.map((c) => c.people.map((p) => p.display_name).join('; ')),
  ])
  return [header, ...lines].map((line) => line.map(csvField).join(',')).join('\r\n') + '\r\n'
}

const downloadCsv = (csv: string, filename: string) => {
  // BOM so Excel opens the file as UTF-8 (en dashes, accented names).
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const Coverage: React.FC = () => {
  const [data, setData] = useState<SmCoverage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hideUnrated, setHideUnrated] = useState(true)
  const [sort, setSort] = useState<SortMode>('coverage')
  const [search, setSearch] = useState('')
  const [hover, setHover] = useState<HoverState | null>(null)

  useEffect(() => {
    getSkillsCoverage()
      .then(setData)
      .catch((e) => setError(e?.message || 'Failed to load coverage'))
  }, [])

  const matcher = useMemo(() => skillMatcher(search), [search])

  const matched = useMemo(() => {
    if (!data) return []
    return matcher ? data.skills.filter((s) => matcher.test(s.name)) : data.skills
  }, [data, matcher])

  const rows = useMemo(() => {
    const visible = hideUnrated ? matched.filter((s) => s.rated_count > 0) : [...matched]
    return visible.sort(
      sort === 'alpha' ? byName : (a, b) => b.coverage - a.coverage || byName(a, b),
    )
  }, [matched, hideUnrated, sort])

  const hiddenCount = matched.length - rows.length

  const exportCsv = () => {
    if (!data) return
    const today = new Date().toISOString().slice(0, 10)
    downloadCsv(coverageCsv(data.levels, rows), `skills-coverage-${today}.csv`)
  }

  const cellStyle = (count: number): React.CSSProperties => ({
    textAlign: 'center',
    padding: '8px 6px',
    fontSize: '14px',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: count > 0 ? 600 : 400,
    color: count > 0 ? 'var(--color-black)' : 'var(--color-light-grey)',
    backgroundColor: cellColour(count, data?.max_cell_count ?? 0),
    border: '2px solid var(--color-white)',
    borderRadius: '6px',
    cursor: count > 0 ? 'default' : undefined,
  })

  return (
    <div>
      <PageHeader
        title="Skills Coverage"
        subtitle="How many people hold each rating on each skill, from current profile ratings"
      />
      <div style={{ padding: '24px' }}>
        {error && <div style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</div>}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px 24px',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: '340px' }}>
            <Search
              size={18}
              style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--color-dark-grey)' }}
            />
            <input
              type="search"
              placeholder="Search skills…"
              aria-label="Search skills"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={hideUnrated}
              onChange={(e) => setHideUnrated(e.target.checked)}
            />
            Hide skills no one is rated on
          </label>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <span style={{ color: 'var(--color-dark-grey)' }}>Sort</span>
            <div style={toggleGroup}>
              <button type="button" style={toggleButton(sort === 'coverage')} onClick={() => setSort('coverage')}>
                Coverage
              </button>
              <button type="button" style={toggleButton(sort === 'alpha')} onClick={() => setSort('alpha')}>
                A–Z
              </button>
            </div>
          </div>
          {data && (
            <span style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
              {rows.length} skill{rows.length === 1 ? '' : 's'} shown
              {matcher && ` of ${data.skills.length}`}
              {hiddenCount > 0 && ` · ${hiddenCount} unrated hidden`}
            </span>
          )}
          {data && (
            <div style={{ marginLeft: 'auto' }}>
              <Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Download size={16} /> Export CSV
                </span>
              </Button>
            </div>
          )}
        </div>

        {data && (
          <div style={{ ...blockStyle, overflowX: 'auto' }}>
            {rows.length === 0 ? (
              <div style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>
                {matcher
                  ? `No skills match “${search.trim()}”${hiddenCount > 0 ? ' among rated skills' : ''}.`
                  : `No skills to show${hideUnrated ? ' — nobody has a current rating yet.' : '.'}`}
              </div>
            ) : (
              // #380 fixed layout: every rating column is the same width and long
              // labels ("Subject Matter Expert") wrap instead of widening theirs.
              <table
                style={{
                  width: '100%',
                  minWidth: `${200 + data.levels.length * RATING_COL_WIDTH + 100}px`,
                  tableLayout: 'fixed',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <colgroup>
                  <col />
                  {data.levels.map((lvl) => (
                    <col key={lvl.value} style={{ width: `${RATING_COL_WIDTH}px` }} />
                  ))}
                  <col style={{ width: '100px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={th}>Skill</th>
                    {data.levels.map((lvl) => (
                      <th key={lvl.value} style={{ ...th, textAlign: 'center', overflowWrap: 'break-word' }}>
                        {lvl.value}
                        {lvl.label && (
                          <div style={{ fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>
                            {lvl.label}
                          </div>
                        )}
                      </th>
                    ))}
                    <th style={{ ...th, textAlign: 'right' }} title="Sum of ratings across people">
                      Coverage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((skill) => (
                    <tr key={skill.skill_id}>
                      <td style={{ padding: '8px 12px', fontSize: '14px', fontWeight: 500 }}>{skill.name}</td>
                      {skill.cells.map((cell) => (
                        <td
                          key={cell.rating}
                          style={cellStyle(cell.count)}
                          onMouseEnter={(e) =>
                            cell.count > 0 &&
                            setHover({ skill: skill.name, cell, x: e.clientX, y: e.clientY })
                          }
                          onMouseMove={(e) =>
                            cell.count > 0 &&
                            setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))
                          }
                          onMouseLeave={() => setHover(null)}
                          aria-label={`${skill.name}, rating ${cell.rating}: ${cell.count} ${
                            cell.count === 1 ? 'person' : 'people'
                          }${cell.count ? ` — ${cell.people.map((p) => p.display_name).join(', ')}` : ''}`}
                        >
                          {cell.count}
                        </td>
                      ))}
                      <td
                        style={{
                          padding: '8px 12px',
                          textAlign: 'right',
                          fontSize: '14px',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {skill.coverage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left: hover.x + 14,
            top: hover.y + 14,
            zIndex: 1000,
            maxWidth: '280px',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '10px 12px',
            fontSize: '13px',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>
            {hover.skill} — rated {hover.cell.rating}
          </div>
          {hover.cell.people.map((p) => (
            <div key={p.user_id}>{p.display_name}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Coverage
